import React, { useState } from "react";
import Card from "components/card";
import { useQueryParams } from "hooks/useQueryParams";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  Table,
  RowData,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { SearchInput } from "./Search";
import { BiPlus } from "react-icons/bi";
import FilterDropdown from "components/table/FilterDropdown";
import { FilterOption } from "components/table/FilterDropdown";
import TooltipHorizon from "components/tooltip";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
    filterable?: boolean;
    filterOptions?: FilterOption[];
  }
}

export interface GenericTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  onAdd?: () => void;
  renderRowClassName?: (
    row: ReturnType<Table<TData>["getRowModel"]>["rows"][0]
  ) => string;
  searchPlaceholder?: string;
  debug?: boolean;
  customQueryName?: string;
  addIcon?: string;
  addLabel?: string;
  onRowClick?: (row: TData) => void;
  maxHeight?: string;
  disabledAddButton?: boolean;
  toolTipTextForAddButton?: string;
}

function GenericTable<TData>({
  data,
  columns,
  onAdd,
  renderRowClassName,
  searchPlaceholder,
  debug = false,
  customQueryName = "q",
  onRowClick,
  addIcon,
  addLabel,
  maxHeight = "max-h-[calc(100vh-200px)]",
  disabledAddButton = false,
  toolTipTextForAddButton,
}: Readonly<GenericTableProps<TData>>) {
  const { t } = useTranslation();
  const { params, updateParams } = useQueryParams(customQueryName);
  const [searchParams] = useSearchParams();

  // Initialize column filters from URL parameters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    const filterableColumns = columns.filter((col) => col.meta?.filterable);

    filterableColumns.forEach((col) => {
      const columnId = (col as any).accessorKey || (col as any).id;
      if (columnId) {
        const filterValue = searchParams.get(`filter_${columnId}`);
        if (filterValue) {
          filters.push({
            id: columnId,
            value: filterValue.split(","),
          });
        }
      }
    });

    return filters;
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: params.query,
      columnFilters,
    },
    onGlobalFilterChange: (value) => updateParams({ query: value }),
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: debug,
    debugHeaders: debug,
    debugColumns: debug,
    filterFns: {
      multiSelect: (row, columnId, filterValue) => {
        if (
          !filterValue ||
          !Array.isArray(filterValue) ||
          filterValue.length === 0
        ) {
          return true;
        }
        const cellValue = row.getValue(columnId);
        return filterValue.includes(String(cellValue));
      },
    },
  });

  const filterableColumns = columns.filter((col) => col.meta?.filterable);
  const effectiveSearchPlaceholder =
    searchPlaceholder ?? t("table.searchPlaceholder");
  const effectiveAddLabel = addLabel ?? t("table.add");

  return (
    <Card
      extra={`relative w-full ${
        maxHeight === "flex-1" ? "flex-1 flex flex-col min-h-0" : maxHeight
      } sm:overflow-auto !shadow-none`}
    >
      <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-4">
        <div className="w-full flex-1">
          <SearchInput
            placeHolder={effectiveSearchPlaceholder}
            onChange={(s) => updateParams({ query: s })}
            value={params.query || ""}
          />
        </div>

        {/* Filter Dropdowns - Always visible next to search */}
        {filterableColumns.length > 0 && (
          <div className="flex flex-wrap items-end gap-3">
            {filterableColumns.map((col, index) => {
              const columnId =
                (col as any).accessorKey ||
                (col as any).id ||
                `column-${index}`;
              const column = table.getColumn(columnId);
              if (!column) return null;
              return (
                <div key={columnId} className="min-w-[140px]">
                  <FilterDropdown
                    column={column}
                    columnId={columnId}
                    table={table}
                    showLabel={true}
                    options={column.columnDef.meta?.filterOptions ?? []}
                  />
                </div>
              );
            })}
          </div>
        )}

        {onAdd && (
          <div className="w-full lg:w-auto">
            {disabledAddButton && toolTipTextForAddButton ? (
              <TooltipHorizon
                trigger={
                  <button
                    disabled={disabledAddButton}
                    className="linear flex h-[45px] w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-gray-400 px-4 text-white opacity-60 transition duration-200 lg:w-fit dark:bg-gray-600"
                  >
                    <span className="text-white">{addIcon ?? "+"}</span>
                    <span>{effectiveAddLabel}</span>
                  </button>
                }
                content={
                  <span className="block max-w-[10rem] whitespace-normal text-center">
                    {toolTipTextForAddButton}
                  </span>
                }
                placement="top"
                extra=""
              />
            ) : (
              <button
                className={`linear flex h-[45px] w-full items-center justify-center gap-2 rounded-xl px-4 text-white transition duration-200 lg:w-fit ${
                  disabledAddButton
                    ? "cursor-not-allowed bg-gray-400 opacity-60 dark:bg-gray-600"
                    : "bg-brand-500 hover:bg-brand-600 active:bg-brand-700"
                }`}
                type="button"
                onClick={onAdd}
                disabled={disabledAddButton}
              >
                <span className="text-white">{addIcon ?? "+"}</span>
                <span>{effectiveAddLabel}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex-1 overflow-auto xl:overflow-x-hidden">
        {table.getRowModel().rows.length > 0 ? (
          <table className="w-full select-none">
            <thead className="border-b bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, colIdx) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`cursor-pointer border-b border-gray-200 p-4 text-left font-medium text-gray-900 hover:text-blue-600 dark:border-white/30 dark:text-gray-100 dark:hover:text-blue-400 ${
                          header.column.columnDef.meta?.headerClassName ?? ""
                        }`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="select-none">
              {table.getRowModel().rows.map((row) => {
                const rowClassName = renderRowClassName
                  ? renderRowClassName(row)
                  : "";
                return (
                  <tr
                    key={row.id}
                    className={`border-b transition-colors dark:border-gray-700 ${
                      onRowClick && "hover:bg-gray-50 dark:hover:bg-gray-800"
                    } ${rowClassName}`}
                  >
                    {row.getVisibleCells().map((cell, cellIdx) => {
                      const isClickable = cellIdx === 0 && onRowClick;
                      const classNames = [
                        "min-w-[150px]",
                        "border-white/0",
                        "p-4",
                        "py-3",
                        "pr-4",
                      ];
                      if (isClickable) {
                        classNames.push("cursor-pointer");
                      }
                      if (cell.column.columnDef.meta?.cellClassName) {
                        classNames.push(
                          cell.column.columnDef.meta.cellClassName
                        );
                      }
                      return (
                        <td
                          key={cell.id}
                          className={classNames.join(" ")}
                          onClick={
                            isClickable
                              ? () => onRowClick(row.original)
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border bg-white p-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {params.query
                ? t("table.empty.noMatching")
                : t("table.empty.empty")}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {params.query ? (
                t("table.empty.tryDifferent")
              ) : (
                <div className="flex flex-col items-center justify-center gap-3">
                  {onAdd ? (
                    <p>{t("table.empty.getStarted")}</p>
                  ) : (
                    <p>{t("table.empty.noItems")}</p>
                  )}

                  <div>
                    {onAdd && (
                      <button
                        className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow transition-colors duration-150 hover:bg-brand-600 sm:h-8 sm:w-auto"
                        type="button"
                        onClick={onAdd}
                      >
                        <span className="text-white">
                          {addIcon ?? <BiPlus size={16} />}
                        </span>
                        <span>{effectiveAddLabel}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

export default GenericTable;
